'use client'

import { X24 } from '@vinted/monochrome-icons'
import {
  Button,
  Cell,
  Dialog,
  Divider,
  InputText,
  Navigation,
  Radio,
  Spacer,
  Validation,
} from '@vinted/web-ui'
import { isFinite, toLower } from 'lodash'
import { ChangeEvent, FormEvent, Fragment, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

import { ErrorItem, useFetch } from '@marketplace-web/core-api/api-client-util'
import { reloadPage } from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import {
  getTransactionPackageSizes,
  selectTransactionPackageSize,
  getCustomShipmentConfig,
  transformGetCustomShipmentConfig,
} from '@marketplace-web/bundles/items-bundle-data'
import { formatCurrencyAmount } from '@marketplace-web/currency/currency-data'
import {
  PackageSizeCode,
  PackageSizeModel,
  transformPackageSizesResponse,
  clickEvent,
} from '@marketplace-web/shipping/package-size-data'

type Props = {
  transactionId: number
  isOpen: boolean
  onClose: () => void
}

const PackageSizesModal = ({ transactionId, isOpen, onClose }: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('conversation.actions.confirm_package_size.modal')

  const { locale } = useIntl()

  const {
    transformedData: customShipmentConfig,
    fetch: fetchGetCustomShipmentConfig,
    isLoading: isCustomShipmentConfigLoading,
  } = useFetch(getCustomShipmentConfig, transformGetCustomShipmentConfig)

  const {
    fetch: fetchTransactionPackageSizes,
    transformedData: transactionPackageSizes,
    error: transactionPackageSizesError,
    isLoading: isTransactionPackageSizesLoading,
  } = useFetch(getTransactionPackageSizes, transformPackageSizesResponse)

  const { packageSizes } = transactionPackageSizes || {}

  const [customShipmentPrice, setCustomShipmentPrice] = useState<string>()
  const [selectedSizeIndex, setSelectedSizeIndex] = useState<number | null>(null)
  const [transactionPackageSizeSelectionUiState, setTransactionPackageSizeSelectionUiState] =
    useState(UiState.Idle)
  const [transactionPackageSizeSelectionError, setTransactionPackageSizeSelectionError] =
    useState<ErrorItem>()

  useEffect(() => {
    fetchGetCustomShipmentConfig()
  }, [fetchGetCustomShipmentConfig])

  useEffect(() => {
    fetchTransactionPackageSizes({ transactionId })
  }, [fetchTransactionPackageSizes, transactionId])

  function isShipmentPriceValid(shipmentPrice?: string) {
    const hasPrice = shipmentPrice !== '' || shipmentPrice !== undefined

    if (!hasPrice || !customShipmentConfig?.prices) return false

    const shipmentPriceAsNumber = Number(shipmentPrice)
    const configPriceMin = Number(customShipmentConfig.prices.minimum.amount)
    const configPriceMax = Number(customShipmentConfig.prices.maximum.amount)

    if (!isFinite(shipmentPriceAsNumber)) return false
    if (shipmentPriceAsNumber > configPriceMax || shipmentPriceAsNumber < configPriceMin)
      return false

    return true
  }

  const handleSizeSelect = (index: number) => () => {
    setSelectedSizeIndex(index)
  }

  function trackPackageSizeSelection(packageSizeId: number) {
    track(
      clickEvent({
        screen: 'message_reply',
        target: 'submit_package_size',
        targetDetails: JSON.stringify({
          parcel_size: packageSizeId,
          transaction_id: transactionId,
        }),
      }),
    )
  }

  async function selectPackageSize(packageSizeId: number, price: number) {
    trackPackageSizeSelection(packageSizeId)

    const response = await selectTransactionPackageSize({
      transactionId,
      packageSizeId,
      customShipmentPrice: price,
    })
    if ('errors' in response) {
      setTransactionPackageSizeSelectionUiState(UiState.Failure)
      setTransactionPackageSizeSelectionError(response.errors[0])
    }

    setTransactionPackageSizeSelectionUiState(UiState.Success)
  }

  function handleConfirmation(event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) {
    if (transactionPackageSizeSelectionUiState === UiState.Pending) {
      event.preventDefault()

      return
    }

    if (selectedSizeIndex === null || !packageSizes) return

    if (
      packageSizes[selectedSizeIndex]!.code === PackageSizeCode.Custom &&
      !isShipmentPriceValid(customShipmentPrice)
    ) {
      return
    }

    selectPackageSize(packageSizes[selectedSizeIndex]!.id, Number(customShipmentPrice))
  }

  function handleInputChange(
    event: ChangeEvent<HTMLInputElement> | FormEvent<HTMLTextAreaElement>,
  ) {
    setCustomShipmentPrice(event.currentTarget.value)
  }

  function isLoading() {
    return (
      transactionPackageSizeSelectionUiState === UiState.Pending || isCustomShipmentConfigLoading
    )
  }

  function renderCustomShipmentPriceContainer(isSelected: boolean, isCustom: boolean) {
    if (!isSelected || (isSelected && !isCustom)) return null
    if (!customShipmentConfig?.prices) return null

    const { minimum } = customShipmentConfig.prices

    return (
      <>
        <Spacer size={Spacer.Size.Large} />
        <InputText
          name="custom_package_size_cost"
          title={translate('input_textarea_title')}
          placeholder={formatCurrencyAmount(minimum, locale)}
          onChange={handleInputChange}
          value={customShipmentPrice}
          styling={InputText.Styling.Tight}
        />
      </>
    )
  }

  function renderPackageSize(packageSize: PackageSizeModel, packageSizeIndex: number) {
    const { id, title, code, description } = packageSize

    const fallbackFormatDescription = translate(`package_description.${toLower(code)}`)

    const isSizeSelected = selectedSizeIndex === packageSizeIndex

    const packageSizeDescription = description || fallbackFormatDescription

    return (
      <Fragment key={id}>
        <Cell
          title={title}
          body={
            <>
              {packageSizeDescription}
              {renderCustomShipmentPriceContainer(
                isSizeSelected,
                packageSize.code === PackageSizeCode.Custom,
              )}
            </>
          }
          clickable
          highlighted={isSizeSelected}
          suffix={
            <Radio
              name="select_package_size"
              checked={isSizeSelected}
              onChange={() => undefined}
              aria={{
                'aria-labelledby': `package-size-cell-${id}-label`,
              }}
            />
          }
          onClick={handleSizeSelect(packageSizeIndex)}
          testId={`package-size-cell-${id}`}
        />
        <Divider />
      </Fragment>
    )
  }

  function renderPackageSizes() {
    return packageSizes?.map((packageSize: PackageSizeModel, index: number) =>
      renderPackageSize(packageSize, index),
    )
  }

  function renderConfirmButton() {
    const isButtonDisabled =
      selectedSizeIndex === null ||
      (!isShipmentPriceValid(customShipmentPrice) &&
        packageSizes![selectedSizeIndex]?.code === PackageSizeCode.Custom)

    return (
      <Button
        text={translate('confirm')}
        onClick={handleConfirmation}
        disabled={isButtonDisabled}
        isLoading={transactionPackageSizeSelectionUiState === UiState.Pending}
        theme="primary"
        styling={Button.Styling.Filled}
        testId="package-sizes-modal-confirm-button"
      />
    )
  }

  function renderLoader() {
    return <ContentLoader testId="package-sizes-loader" styling={ContentLoader.Styling.Wide} />
  }

  function renderTransactionPackageSizesError() {
    if (isTransactionPackageSizesLoading) return null

    const { value: errorValue } = transactionPackageSizesError?.errors[0] || {}

    if (!errorValue) return null

    return (
      <div className="u-flexbox u-justify-content-center">
        <Validation text={errorValue} theme="warning" />
      </div>
    )
  }

  function renderSelectTransactionPackageSizeError() {
    if (transactionPackageSizeSelectionUiState !== UiState.Failure) return null

    const { value: errorValue } = transactionPackageSizeSelectionError || {}

    if (!errorValue) return null

    return (
      <div className="u-flexbox u-justify-content-center">
        <Validation text={errorValue} theme="warning" />
      </div>
    )
  }

  if (transactionPackageSizeSelectionUiState === UiState.Success) reloadPage()

  return (
    <Dialog show={isOpen} defaultCallback={onClose}>
      <Navigation
        right={
          <Button
            styling={Button.Styling.Flat}
            onClick={onClose}
            iconName={X24}
            inline
            aria={{
              'aria-label': translate('a11y_close_icon'),
            }}
          />
        }
        body={translate('title')}
      />
      <Divider />
      <div className="modal-contained-content">
        {isLoading() ? renderLoader() : renderPackageSizes()}
      </div>
      {renderSelectTransactionPackageSizeError()}
      <div className="u-ui-margin-large">
        {renderConfirmButton()}
        {renderTransactionPackageSizesError()}
      </div>
    </Dialog>
  )
}

export default PackageSizesModal
